import type { SharedData } from '../../types';
import type { InstagramAccount } from '../types';

import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { useImageUpload } from '@valwin/facto/hooks';

import { uploadAsset } from '~entities/asset';

import { ShareModal } from '../../ui/ShareModal';
import { usePublishInstagramPost } from '../model/usePublishInstagramPost';
import { InstagramShareFormProvider } from './context';
import { registerInstagramShareOpener } from './shareCommand';
import { type InstagramShareFormValues, instagramShareSchema } from './types';
import { InstagramShareForm } from './ui/InstagramShareForm';
import { InstagramSharePreview } from './ui/InstagramSharePreview';
import { useEnsureInstagramConnection } from './useEnsureInstagramConnection';
import { useInstagramImageAdaptation } from './useInstagramImageAdaptation';
import { useReopenShare } from './useReopenShare';
import { useShareToast } from './useShareToast';

type InstagramShareHostProps = {
  pharmacyId: string;
};

/**
 * Central host for the Instagram share flow, mounted once at the app root
 * (see AuthenticatedRoute) next to InstagramShareFeedbackListener.
 *
 * Why a root host instead of a per-row hook like Facebook: publishing is
 * asynchronous. The POST returns early and the real outcome arrives later over
 * websocket (eventBus), by which point the row that triggered the share may
 * have unmounted. The host, its toast queue and the listener must outlive the
 * caller, so they cannot live inside the row.
 *
 * Why no React context: rows only fire a command, they read no shared state.
 * They call openInstagramShare (shareCommand), which forwards to the opener
 * this host registers — no provider wrapping the tree, no re-render on rows.
 *
 * Responsibilities are split around this host: connection gate
 * (useEnsureInstagramConnection), toasts (useShareToast), reopen-on-failure
 * (useReopenShare), and async resolution (InstagramShareFeedbackListener).
 */
export function InstagramShareHost({ pharmacyId }: InstagramShareHostProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [sharedData, setSharedData] = useState<SharedData | null>(null);
  const [account, setAccount] = useState<InstagramAccount | null>(null);

  const { ensureConnected } = useEnsureInstagramConnection(pharmacyId);
  const publishMutation = usePublishInstagramPost();
  const { start, resolve, drop } = useShareToast();
  const reopen = useReopenShare(pharmacyId);

  const { handleUpload } = useImageUpload(async (file: File) => {
    const { assetUrl } = await uploadAsset(pharmacyId, file);

    return assetUrl;
  });

  const form = useForm<InstagramShareFormValues>({
    resolver: zodResolver(instagramShareSchema),
    defaultValues: { caption: '', imageUrls: [] },
  });

  const imageAdaptation = useInstagramImageAdaptation({
    form,
    uploadImage: handleUpload,
  });

  const { reset: resetImageAdaptation } = imageAdaptation;

  const showModal = useCallback(
    (data: SharedData) => {
      resetImageAdaptation();
      form.reset({
        caption: data.postMessage,
        imageUrls: data.imageUrl ? [data.imageUrl] : [],
      });
      setSharedData(data);
      setIsOpen(true);
    },
    [form, resetImageAdaptation]
  );

  const open = useCallback(
    async (data: SharedData) => {
      const connectedAccount = await ensureConnected();

      if (connectedAccount) {
        setAccount(connectedAccount);
        showModal(data);
      }
    },
    [ensureConnected, showModal]
  );

  useEffect(() => registerInstagramShareOpener(open), [open]);

  const close = () => setIsOpen(false);

  const handleSubmit = form.handleSubmit((values) => {
    const source = sharedData?.source;

    if (!source) {
      return;
    }

    const toastId = start(source);

    close();

    publishMutation.mutate(
      {
        pharmacyId,
        params: {
          imageUrls: values.imageUrls,
          caption: values.caption,
          source,
        },
      },
      {
        onError: () => {
          drop(toastId);
          resolve(toastId, 'error', () =>
            reopen({
              postMessage: values.caption,
              imageUrl: values.imageUrls[0] ?? '',
              shareUrl: sharedData?.shareUrl ?? '',
              source,
            })
          );
        },
      }
    );
  });

  return (
    <InstagramShareFormProvider form={form}>
      <ShareModal
        open={isOpen}
        onClose={close}
        title={t('integrations instagram share title')}
        form={<InstagramShareForm imageAdaptation={imageAdaptation} />}
        preview={
          <InstagramSharePreview
            account={account}
            isImageProcessing={imageAdaptation.status === 'processing'}
          />
        }
        onSubmit={handleSubmit}
      />
    </InstagramShareFormProvider>
  );
}
